import SaveModeEnum from '@/enums/SaveModeEnum';
import TreeNode from '@/types/TreeNode';
import { BvMsgBoxData } from 'bootstrap-vue/src/components/modal';
import { MAIN_FILTER_TREE } from '@/App.settings';
import Pin from '@/types/FilterTree/Pin';
import SelectModeEnum from '@/enums/SelectModeEnum';
import INodeSelector from '@/interfaces/INodeSelector';

/** Médiateur de communication inter-composants/stores VueX dédié à la navigation */
export class NavigationMediator {
	private context!: Vue;

	/** Instance du composant gèrant le chargement des composants de feature */
	private featureManager: Vue & INodeSelector & IFeatureManager;

	/** Liste d'instance de composants implémentant l'interface INodeSelector */
	private components: Set<Vue & INodeSelector> = new Set<Vue & INodeSelector>();

	/** Constructeur
	 * @param context Vue pour accès à $t, $bvModal et $store
	 * @todo Pourquoi ne pas seulement passé $t, $bvModal et $store plutôt que Vue complet ??!!🤔
	 */
	constructor(context: Vue) {
		this.context = context;
	}

	/** 
	 * Déclare un composant comme devant être notifié par le changement de noeud sélectionné (en SingleMode)
	 * @param component Composant implémentant l'interface INodeSelector gérant le noeud sélectionné
	 * @description Attention ! seul les composants non IFeatureManager doivent être déclaré ici !
	 * @todo Bloquer l'ajout de component implémentant IFeatureManager
	 */
	public Register(component: Vue & INodeSelector) {
		this.components.add(component);
	}

	/** Déclare le composant gèrant le chargement des composants de feature */
	public RegisterFeatureManager(component: Vue & INodeSelector & IFeatureManager) {
		this.featureManager = component;
		this.Register(component);
	}

	/** Désinscrit le composant de la liste des composants devant être notifié sur le changement de noeud sélectionné */
	public Unregister(component: Vue & INodeSelector) {
		this.components.delete(component);
	}

	/** Indique au composant implémentant IFeatureManager qu'un nouvel objet est en cours de création afin qu'il propose un rendu spécifique
	 * @see FeatureContainer.Vue
	 */
	public NewObject() {
		this.featureManager.OnNewObject();
	}

	/** 
	 * Annulation mode NewObject (création d'un nouvel objet) avec application d'un noeud en SingleNode si Ok
	 * @param node 			 Noeud à passer en SingleNode
	 * @param scrollIntoView Indique si l'arborescence doit être scrollée au niveau du SingleNode
	 *  */
	public async CancelNew(node: TreeNode, scrollIntoView: boolean = true) {
		let data: BvMsgBoxData = await this.context.$bvModal.msgBoxConfirm(this.context.$t('ConfirmObjectCreation').toString(), {
			title: this.context.$t('CancelCreation').toString(), okVariant: 'danger', okTitle: this.context.$t('Yes').toString(), cancelTitle: this.context.$t('No').toString(), hideHeaderClose: false, centered: true, modalClass: 'text-center'
		});

		if (data == true)
			this.featureManager.OnCancelNew();
	}

	/** 
	 * Sélection d'un noeud avec gestion du clic dans l'arborescence alors qu'un objet est en cours de création
	 * @param node 			 		Noeud à sélectionner
	 * @param scrollIntoView 		Indique si l'arborescence doit être scrollé au niveau du noeud sélectionné
	 * @description Appel de SetNode(node) sur les composants implémentant INodeSelector
	 **/
	public async SelectNode(node: TreeNode, scrollIntoView: boolean = true) {
		if (this.context.$store.getters["ObjectStore/GetSaveMode"] == SaveModeEnum.NewObject)
			this.CancelNew(node, scrollIntoView);
		else {
			this.featureManager.ReloadFeature();
			
			/** Appel de SetNode(node) sur les composants implémentant INodeSelector */
			const promises = Array.from(this.components).map(c => c.SetNode(node));
			const results = await Promise.allSettled(promises);

			results.forEach((result, index) => {
				if (result.status === 'rejected')
					console.warn(`Le composant n°${index} n'a pas pu se mettre à jour`);
			});
		}
	}

	/** Renvoi le noeud sélectionné dans l'arborescence */
	private getSelectedNode(): TreeNode {
		if(this.context.$store.getters[MAIN_FILTER_TREE + "/GetSelectMode"] === SelectModeEnum.Single) {
			let pins: Pin[] = this.context.$store.getters[MAIN_FILTER_TREE + "/GetPins"];
			let node: TreeNode = undefined
			if (pins.length > 0)
				node = pins[0].TreeNode;
			return node;
		}
		else
			return undefined;
	}
}

/** Interface pour indiquer qu'un composant gère le chargement des composants de feature */
export interface IFeatureManager extends INodeSelector {
	/** Traitements à réaliser lors de la création d'un nouvel objet */
	OnNewObject();
	/** Traitements à réaliser lors de l'Annulation de la création d'un nouvel objet */
	OnCancelNew()
	/** Méthode pour recharger la feature en cours */
	ReloadFeature();
}